import { escapeHtml } from '../lib/escape';
import type { PhotoQuestion } from '../types';
import { renderPromptsSection } from './prompt-list';

export function renderPhotoBlock(
  q: PhotoQuestion,
  index: number,
  editMode = false,
  locked = false,
): string {
  const previewSrc = q.file ? URL.createObjectURL(q.file) : '';
  const preview = previewSrc
    ? `<img class="photo-preview" src="${previewSrc}" alt="Selected photo preview" />`
    : `
        <span class="photo-dropzone-label">Drop a photo or click to upload</span>
        <span class="photo-dropzone-hint">${escapeHtml(q.description)}</span>
      `;

  const header = editMode
    ? `
      <div class="q-header" data-action="toggle">
        <span class="q-number">PH</span>
        <input
          class="q-title-input"
          type="text"
          value="${escapeHtml(q.title)}"
          data-action="title"
          data-stop-toggle
        />
        <span class="q-toggle"></span>
      </div>
    `
    : `
      <div class="q-header">
        <span class="q-number">PH</span>
        <span class="q-title-static">${escapeHtml(q.title)}</span>
        ${renderRandomModeBtn(q.randomMode)}
        ${renderLockBtn(locked)}
      </div>
    `;

  const metaInputs = editMode
    ? `
      <div class="q-meta-row">
        <label class="q-meta-field">
          <span class="q-meta-label">Heading</span>
          <input
            class="q-meta-input"
            type="text"
            value="${escapeHtml(q.heading)}"
            data-action="heading"
            placeholder="e.g. You in the Story"
          />
        </label>
        <label class="q-meta-field">
          <span class="q-meta-label">Hint</span>
          <input
            class="q-meta-input"
            type="text"
            value="${escapeHtml(q.hint)}"
            data-action="hint"
            placeholder="One-line label hint shown above the question"
          />
        </label>
      </div>
    `
    : '';

  const promptsSection = editMode ? renderPromptsSection(q.prompts, -1) : '';

  return `
    <div class="question-block${locked && !editMode ? ' locked' : ''}" data-q-index="${index}" data-color="3" data-kind="photo">
      ${header}
      <div class="question-body">
        ${metaInputs}
        <label class="photo-dropzone" data-action="photo-dropzone">
          ${preview}
          <input class="photo-hidden-input" type="file" accept="image/*" data-action="photo-file" />
        </label>
        <div class="photo-actions">
          <button type="button" class="photo-action-btn" data-action="photo-webcam">
            Use webcam
          </button>
          <button type="button" class="photo-action-btn" data-action="photo-portrait">
            Choose portrait
          </button>
          ${
            q.file
              ? `<button type="button" class="photo-action-btn" data-action="photo-clear">Remove</button>`
              : ''
          }
        </div>
        ${promptsSection}
      </div>
    </div>
  `;
}

function renderLockBtn(locked: boolean): string {
  const lockedIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
  const unlockedIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>`;
  return `<button type="button" class="q-lock-btn${locked ? ' locked' : ''}" data-action="toggle-lock" aria-label="${locked ? 'Unlock' : 'Lock'} selection" title="${locked ? 'Unlock' : 'Lock'} — exclude from randomize">${locked ? lockedIcon : unlockedIcon}</button>`;
}

function renderRandomModeBtn(mode: 'shuffle' | 'cycle'): string {
  const shuffleIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>`;
  const cycleIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>`;
  const isCycle = mode === 'cycle';
  const label = isCycle ? 'Cycle through options' : 'Shuffle options randomly';
  return `<button type="button" class="q-random-mode-btn${isCycle ? ' cycle' : ''}" data-action="toggle-random-mode" aria-label="${label}" title="${label}">${isCycle ? cycleIcon : shuffleIcon}</button>`;
}
